import React, { useEffect } from 'react';
import { Head } from 'vite-react-ssg';

const PrivacyPolicy = () => {
  // Disable Google AdSense on this page
  useEffect(() => {
    // Find and pause/hide AdSense ads on this page
    const adsByGoogle = document.querySelectorAll('.adsbygoogle');
    adsByGoogle.forEach((ad) => {
      (ad as HTMLElement).style.display = 'none';
    });

    // Also prevent new ads from loading by setting a flag
    (window as any).adsbygoogle = (window as any).adsbygoogle || [];
    (window as any).adsbygoogle.pauseAdRequests = 1;

    return () => {
      // Re-enable ads when leaving this page
      (window as any).adsbygoogle = (window as any).adsbygoogle || [];
      (window as any).adsbygoogle.pauseAdRequests = 0;
      adsByGoogle.forEach((ad) => {
        (ad as HTMLElement).style.display = '';
      });
    };
  }, []);

  return (
    <>
      <Head>
        <title>Privacy Policy - DeepSeek AI Sidebar Extension</title>
        <meta 
          name="description" 
          content="Privacy Policy for DeepSeek AI Sidebar Extension. Learn how we handle your information when using our browser extension." 
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://deepseek.ai/privacy-policy" />
      </Head>

      <main className="min-h-screen pt-24 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Privacy Policy for DeepSeek AI Sidebar Extension</h1>
            
            <p className="text-gray-600 mb-2">
              <strong>Last Updated:</strong> January 12, 2026
            </p>
            <p className="text-gray-600 mb-2">
              <strong>Developer:</strong> Extchange
            </p>
            <p className="text-gray-600 mb-8">
              <strong>Contact:</strong> info@extchange.com
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Introduction</h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                This Privacy Policy describes how the "DeepSeek AI Sidebar" browser extension ("Extension", "we", "our", or "us") handles your information. We are committed to protecting your privacy and being transparent about our data practices.
              </p>
              <p className="text-gray-700 leading-relaxed">
                By installing and using this Extension, you agree to the terms of this Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Single Purpose</h2>
              <p className="text-gray-700 leading-relaxed">
                This Extension provides quick access to the DeepSeek AI chat service directly from your browser toolbar. Users can open the DeepSeek chat window with a single click and use the AITOPIA AI sidebar on any webpage.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">User Preferences (Stored Locally Only)</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                We store the following preferences locally on your device using Chrome's storage API:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Popup Display Settings:</strong> Whether to show the popup window when clicking the extension icon.</li>
                <li><strong>Privacy Consent Status:</strong> A flag indicating whether you have explicitly consented to the sidebar accessing page content.</li>
              </ul>
              <p className="text-gray-700 mb-4 leading-relaxed">
                <strong>Purpose:</strong> To remember your settings across browser sessions, provide a personalized experience, and ensure we do not access page content without your permission.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Storage Location:</strong> Locally on your device via <code className="bg-gray-100 px-1 rounded">chrome.storage.local</code> or <code className="bg-gray-100 px-1 rounded">chrome.storage.sync</code>. This data may sync across your Chrome browsers if you are signed into Chrome sync, but is never sent to our servers.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Information We Do NOT Collect</h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                We want to be absolutely clear about what we <strong>do not</strong> collect:
              </p>
              <ul className="text-gray-700 space-y-2">
                <li>❌ Personally identifiable information (name, email, address, phone number)</li>
                <li>❌ Health information</li>
                <li>❌ Financial or payment information</li>
                <li>❌ Authentication credentials (passwords, PINs, security questions)</li>
                <li>❌ Personal communications (emails, texts, chat messages)</li>
                <li>❌ Location data (GPS, IP address, region)</li>
                <li>❌ User activity data (clicks, mouse movements, keystrokes) associated with your identity</li>
                <li>❌ AI conversation content or chat history (these remain between you and the AI provider)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">No External Data Transmission</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                We do not transmit any data to external servers.
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>No analytics are collected</li>
                <li>No usage data is sent anywhere</li>
                <li>No tracking pixels or beacons are used</li>
                <li>No third-party analytics services are integrated</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                All Extension functionality operates entirely within your browser.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">How We Use Your Information</h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                We use the collected information solely for:
              </p>
              <ol className="list-decimal pl-6 text-gray-700">
                <li><strong>Saving Your Preferences:</strong> Remembering your display settings and consent choice.</li>
                <li><strong>Providing Functionality:</strong> Injecting the AITOPIA sidebar on webpages you visit (only after you grant permission).</li>
              </ol>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Sharing</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">We Do Not Share Any Data</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                We do not sell, trade, rent, or otherwise transfer any information to third parties. Period.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Third-Party AI Services</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                When you use this Extension to access DeepSeek or AITOPIA:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Your interactions with DeepSeek are governed by <a href="https://cdn.deepseek.com/policies/en-US/deepseek-privacy-policy.html?locale=en_US" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">DeepSeek's Privacy Policy</a></li>
                <li>Your interactions with AITOPIA are governed by <a href="https://www.aitopia.ai/privacy-policy/" className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">AITOPIA's Privacy Policy</a></li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                This Extension acts as a convenience tool to access these services. We do not intercept, store, or transmit your conversations with these AI services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Permissions Explained</h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                This Extension requires certain browser permissions to function:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300 mb-4">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-2 text-left">Permission</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Why It's Needed</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2 font-mono text-sm">storage</td>
                      <td className="border border-gray-300 px-4 py-2">To save your preferences (popup settings and consent status) locally on your device.</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2 font-mono text-sm">system.display</td>
                      <td className="border border-gray-300 px-4 py-2">To center popup windows on your screen for a better user experience.</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2 font-mono text-sm">host_permissions (&lt;all_urls&gt;)</td>
                      <td className="border border-gray-300 px-4 py-2">To enable the AITOPIA AI sidebar on the webpages you visit.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-gray-700 leading-relaxed">
                <strong>Important Note:</strong> The <code className="bg-gray-100 px-1 rounded">&lt;all_urls&gt;</code> permission is required to act as a sidebar on every page. We access page content <strong>solely to inject and render the sidebar interface</strong> on the pages you visit. This activity occurs <strong>only after you have explicitly granted consent</strong> via our in-page banner. We do not transmit this page content to our servers.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Rights and Control</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Clear Your Data</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                You can clear all Extension data at any time:
              </p>
              <ol className="list-decimal pl-6 text-gray-700 mb-4">
                <li>Go to <code className="bg-gray-100 px-1 rounded">chrome://extensions/</code></li>
                <li>Find "DeepSeek AI Sidebar"</li>
                <li>Click "Details"</li>
                <li>Click "Clear data" under "Site access" (or "Extension options")</li>
              </ol>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Uninstall</h3>
              <p className="text-gray-700 leading-relaxed">
                Uninstalling the Extension removes all locally stored preferences. No data remains on our servers because we never collected any.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                This Extension is not directed at children under the age of 13. We do not knowingly collect any information from anyone, including children.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. Changes will be indicated by updating the "Last Updated" date at the top of this policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Compliance</h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                This Extension complies with:
              </p>
              <ul className="list-disc pl-6 text-gray-700">
                <li>Google Chrome Web Store Developer Program Policies</li>
                <li>General Data Protection Regulation (GDPR) principles</li>
                <li>California Consumer Privacy Act (CCPA) principles</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 leading-relaxed">
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <p className="text-gray-700 mt-2">
                <strong>Email:</strong> <a href="mailto:info@extchange.com" className="text-blue-600 hover:text-blue-800">info@extchange.com</a>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Certifications</h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                We certify that:
              </p>
              <ul className="text-gray-700 space-y-2">
                <li>✅ We do not sell or transfer user data to third parties</li>
                <li>✅ We do not use or transfer user data for purposes unrelated to the Extension's single purpose</li>
                <li>✅ We do not use or transfer user data to determine creditworthiness or for lending purposes</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Summary</h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-2 text-left">Aspect</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2 font-semibold">Data Collected</td>
                      <td className="border border-gray-300 px-4 py-2">User preferences & Consent status</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2 font-semibold">Data Storage</td>
                      <td className="border border-gray-300 px-4 py-2">Locally on your device only</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2 font-semibold">External Transmission</td>
                      <td className="border border-gray-300 px-4 py-2">None - absolutely no data sent to external servers</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2 font-semibold">Third-Party Sharing</td>
                      <td className="border border-gray-300 px-4 py-2">None</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2 font-semibold">Your Control</td>
                      <td className="border border-gray-300 px-4 py-2">Full control - clear data or uninstall anytime</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <hr className="my-8 border-gray-300" />

            <p className="text-gray-600 italic text-center">
              This privacy policy is effective as of the "Last Updated" date above.
            </p>
          </div>
        </div>
      </main>
    </>
  );
};

export default PrivacyPolicy;
